/**
 * After a work-order invoice is created, offer the documents that actually
 * exist for that invoice (receipt, invoice copy, delivery note) so the
 * counter can open them in one click (#2127).
 *
 * The delivery note ("Lähete") is the server PDF without prices (#4111). The
 * stored selection key stays `deliveryReport` so saved choices carry over.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useNotify } from '@/hooks/useNotify';
import { openInvoicePdf, openReceiptPdf } from '@/hooks/useInvoices';
import { openWorkOrderDeliveryNotePdf, type WorkOrderDetail } from '@/hooks/useWorkOrders';
import { translateBillingError } from '@/lib/billingErrors';

/** Same `vs-` prefix as work-order detail card order (`vs-workorder-detail-card-order`). */
export const POST_INVOICE_DOCUMENTS_STORAGE_KEY = 'vs-post-invoice-documents';

type PostInvoiceDocumentKey = 'receipt' | 'invoiceCopy' | 'deliveryReport';

/** Fixed order the windows open in, so tabs line up the same way every time. */
const DOCUMENT_ORDER: readonly PostInvoiceDocumentKey[] = ['receipt', 'invoiceCopy', 'deliveryReport'];

const DOCUMENT_LABEL_KEYS: Record<PostInvoiceDocumentKey, string> = {
  receipt: 'postInvoiceDocuments.receipt',
  invoiceCopy: 'postInvoiceDocuments.invoiceCopy',
  deliveryReport: 'postInvoiceDocuments.deliveryReport',
};

export interface PostInvoiceDocumentSelection {
  receipt: boolean;
  invoiceCopy: boolean;
  deliveryReport: boolean;
}

const DEFAULT_SELECTION: PostInvoiceDocumentSelection = {
  receipt: true,
  invoiceCopy: true,
  deliveryReport: true,
};

export interface PostInvoiceDocumentsDialogProps {
  open: boolean;
  onClose: () => void;
  invoiceId?: string;
  receiptId?: string;
  workOrder?: WorkOrderDetail;
}

function loadSelection(): PostInvoiceDocumentSelection {
  try {
    const raw = localStorage.getItem(POST_INVOICE_DOCUMENTS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SELECTION };
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return { ...DEFAULT_SELECTION };
    const obj = parsed as Record<string, unknown>;
    return {
      receipt: typeof obj.receipt === 'boolean' ? obj.receipt : DEFAULT_SELECTION.receipt,
      invoiceCopy: typeof obj.invoiceCopy === 'boolean' ? obj.invoiceCopy : DEFAULT_SELECTION.invoiceCopy,
      deliveryReport: typeof obj.deliveryReport === 'boolean'
        ? obj.deliveryReport
        : DEFAULT_SELECTION.deliveryReport,
    };
  } catch {
    return { ...DEFAULT_SELECTION };
  }
}

function saveSelection(selection: PostInvoiceDocumentSelection): void {
  try {
    localStorage.setItem(POST_INVOICE_DOCUMENTS_STORAGE_KEY, JSON.stringify(selection));
  } catch {
    // Ignore quota / private-mode failures, matching other portal preferences.
  }
}

export function PostInvoiceDocumentsDialog({
  open,
  onClose,
  invoiceId,
  receiptId,
  workOrder,
}: PostInvoiceDocumentsDialogProps) {
  const { t } = useTranslation('invoices');
  const { t: tw } = useTranslation('workorders');
  const notify = useNotify();
  const [selection, setSelection] = useState<PostInvoiceDocumentSelection>(loadSelection);
  const [blockedDocuments, setBlockedDocuments] = useState<PostInvoiceDocumentKey[]>([]);

  const showReceipt = Boolean(receiptId);
  const showInvoiceCopy = Boolean(invoiceId);
  const showDeliveryReport = workOrder !== undefined;

  useEffect(() => {
    if (!open) return;
    setSelection(loadSelection());
    setBlockedDocuments([]);
  }, [open]);

  const updateSelection = (key: keyof PostInvoiceDocumentSelection, checked: boolean) => {
    setSelection((current) => {
      const next = { ...current, [key]: checked };
      saveSelection(next);
      return next;
    });
  };

  const receiptSelected = showReceipt && selection.receipt;
  const invoiceCopySelected = showInvoiceCopy && selection.invoiceCopy;
  const deliveryReportSelected = showDeliveryReport && selection.deliveryReport;
  const hasSelection = receiptSelected || invoiceCopySelected || deliveryReportSelected;

  const isSelected = (doc: PostInvoiceDocumentKey): boolean => {
    if (doc === 'receipt') return receiptSelected;
    if (doc === 'invoiceCopy') return invoiceCopySelected;
    return deliveryReportSelected;
  };

  /** The delivery note's name comes from the work-order texts, not the invoice ones. */
  const documentLabel = (doc: PostInvoiceDocumentKey): string =>
    doc === 'deliveryReport' ? tw('deliveryNote.documentLabel') : t(DOCUMENT_LABEL_KEYS[doc]);

  /** Fill a window that was opened in the click handler with one document. */
  const loadDocument = (doc: PostInvoiceDocumentKey, win: Window) => {
    if (doc === 'receipt' && receiptId) {
      void openReceiptPdf(receiptId, win).catch((err: unknown) => {
        notify.error(t('payments.openReceiptFailed'), translateBillingError(err, t));
      });
    } else if (doc === 'invoiceCopy' && invoiceId) {
      void openInvoicePdf(invoiceId, win).catch((err: unknown) => {
        notify.error(t('actions.previewInvoiceFailed'), translateBillingError(err, t));
      });
    } else if (doc === 'deliveryReport' && workOrder) {
      // Closes the window itself when the download fails; the error is ours to show.
      void openWorkOrderDeliveryNotePdf(workOrder.id, win).catch(() => {
        notify.error(tw('deliveryNote.documentLabel'), tw('deliveryNote.openFailed'));
      });
    } else {
      win.close();
    }
  };

  const handleOpenSelected = () => {
    if (!hasSelection) return;
    saveSelection(selection);
    // Browser popup blocker: every window must be opened here, synchronously,
    // before any await (#2127). Opening them all first and only then starting
    // the downloads keeps a slow PDF from eating the gesture of the next one
    // (#4093). Browsers that still allow just one popup per click return null
    // for the rest; those documents stay in the dialog with their own button.
    const docs = DOCUMENT_ORDER.filter(isSelected);
    const windows = docs.map(() => window.open('', '_blank'));
    const blocked: PostInvoiceDocumentKey[] = [];
    docs.forEach((doc, index) => {
      const win = windows[index];
      if (win) loadDocument(doc, win);
      else blocked.push(doc);
    });
    if (blocked.length === 0) {
      onClose();
      return;
    }
    setBlockedDocuments(blocked);
    notify.warning(
      t('postInvoiceDocuments.popupBlocked'),
      t('postInvoiceDocuments.popupBlockedDesc', { count: blocked.length }),
    );
  };

  /** Each blocked document gets its own click, i.e. its own popup allowance. */
  const handleOpenBlocked = (doc: PostInvoiceDocumentKey) => {
    const win = window.open('', '_blank');
    if (!win) {
      notify.warning(
        t('postInvoiceDocuments.popupBlocked'),
        t('postInvoiceDocuments.popupBlockedDesc', { count: blockedDocuments.length }),
      );
      return;
    }
    loadDocument(doc, win);
    const remaining = blockedDocuments.filter((d) => d !== doc);
    setBlockedDocuments(remaining);
    if (remaining.length === 0) onClose();
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={onClose}
      mobileTitle={t('postInvoiceDocuments.title')}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t('postInvoiceDocuments.title')}</DialogTitle>
      <DialogContent data-testid="post-invoice-documents-dialog">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('postInvoiceDocuments.description')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {showReceipt && (
            <FormControlLabel
              data-testid="post-invoice-document-receipt"
              control={
                <Checkbox
                  checked={selection.receipt}
                  onChange={(event) => updateSelection('receipt', event.target.checked)}
                />
              }
              label={t('postInvoiceDocuments.receipt')}
            />
          )}
          {showInvoiceCopy && (
            <FormControlLabel
              data-testid="post-invoice-document-invoice"
              control={
                <Checkbox
                  checked={selection.invoiceCopy}
                  onChange={(event) => updateSelection('invoiceCopy', event.target.checked)}
                />
              }
              label={t('postInvoiceDocuments.invoiceCopy')}
            />
          )}
          {showDeliveryReport && (
            <FormControlLabel
              data-testid="post-invoice-document-delivery-report"
              control={
                <Checkbox
                  checked={selection.deliveryReport}
                  onChange={(event) => updateSelection('deliveryReport', event.target.checked)}
                />
              }
              label={documentLabel('deliveryReport')}
            />
          )}
        </Box>
        {blockedDocuments.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }} data-testid="post-invoice-blocked">
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t('postInvoiceDocuments.blockedHint')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {blockedDocuments.map((doc) => (
                <Button
                  key={doc}
                  size="small"
                  variant="outlined"
                  onClick={() => handleOpenBlocked(doc)}
                  data-testid={`post-invoice-open-blocked-${doc}`}
                >
                  {t('postInvoiceDocuments.openDocument', {
                    document: documentLabel(doc),
                  })}
                </Button>
              ))}
            </Box>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="post-invoice-skip">
          {t('postInvoiceDocuments.skip')}
        </Button>
        <Button
          variant="contained"
          onClick={handleOpenSelected}
          disabled={!hasSelection}
          data-testid="post-invoice-open-selected"
        >
          {t('postInvoiceDocuments.openSelected')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
